import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { money } from "./Give";

const STATUS_LABEL = { active: "Active", trialing: "Active", past_due: "Payment issue", cancelled: "Ended", canceled: "Ended", paid: "Received", refunded: "Refunded", unpaid: "Payment issue", incomplete: "Pending" };

export function GiftStatusCard({ gift, onCancel, onPortal }) {
  const [busy, setBusy] = useState(false);
  const monthly = gift.giving_type === "monthly";
  const ended = ["cancelled", "canceled"].includes(gift.status);
  const scheduled = !!(gift.cancel_at_period_end || gift.cancel_requested_at) && !ended;
  const cancel = async () => {
    if (!window.confirm(`Cancel monthly support? It stays active through ${(gift.current_period_end || "").slice(0, 10) || "the end of the current billing period"}; no further charges will be made.`)) return;
    setBusy(true);
    try { await onCancel(); toast("Monthly support cancelled. No further charges will be made."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Unable to cancel right now."); } finally { setBusy(false); }
  };
  const portal = async () => {
    setBusy(true);
    try { const url = await onPortal(); window.location.href = url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Secure billing management is unavailable right now."); setBusy(false); }
  };
  return (
    <div className="rounded-2xl border p-5" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }} data-testid={`gift-card-${gift.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="eyebrow">{monthly ? "Monthly support" : "One-time gift"}</div>
          <div className="font-serif text-2xl" style={{ color: "var(--burgundy)" }} data-testid="gift-amount">{money(gift.amount_cents, gift.currency)}{monthly ? " / month" : ""}</div>
          <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
            {monthly ? <>Started {(gift.started_at || gift.created_at || "").slice(0, 10)}</> : <>{(gift.paid_at || gift.created_at || "").slice(0, 10)}</>}
            {monthly && gift.current_period_end && !ended && <> · {scheduled ? "Ends" : "Next billing"} {gift.current_period_end.slice(0, 10)}</>}
            {ended && gift.cancelled_at && <> · Ended {gift.cancelled_at.slice(0, 10)}</>}
          </div>
        </div>
        <span className="rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-widest" data-testid="gift-status"
          style={{ background: scheduled ? "var(--gold-soft)" : ended || gift.status === "refunded" ? "var(--dark-card)" : gift.status === "past_due" ? "var(--rose)" : "var(--gold)", color: ended || gift.status === "refunded" ? "var(--bg-cream)" : "var(--burgundy)" }}>
          {scheduled ? "Cancellation scheduled" : STATUS_LABEL[gift.status] || gift.status}
        </span>
      </div>
      {monthly && scheduled && <p className="mt-3 text-sm" style={{ color: "var(--text-obsidian)" }} data-testid="gift-cancel-notice">Your monthly support has been cancelled and remains active through <strong>{(gift.current_period_end || "").slice(0, 10)}</strong>. No further charges will be made.</p>}
      {monthly && !ended && onCancel && (
        <div className="mt-4 flex flex-wrap gap-3 text-sm">
          {onPortal && <button data-testid="gift-manage-portal-btn" disabled={busy} onClick={portal} className="rounded-full px-4 py-2 text-xs font-medium border disabled:opacity-50" style={{ borderColor: "var(--burgundy)", color: "var(--burgundy)" }}>Manage billing securely (Stripe)</button>}
          {!scheduled && <button data-testid="gift-cancel-btn" disabled={busy} onClick={cancel} className="rounded-full px-4 py-2 text-xs font-medium disabled:opacity-50" style={{ background: "var(--rose-dark)", color: "var(--bg-cream)" }}>Cancel monthly support</button>}
        </div>
      )}
    </div>
  );
}

export default function ManageGiving() {
  const { token } = useParams();
  const [gift, setGift] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = () => api.get(`/giving/manage/${token}`).then((r) => setGift(r.data)).catch(() => setGift(null)).finally(() => setLoading(false));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 lg:py-16" data-testid="manage-giving-page">
      <div className="eyebrow mb-2">Support the Temple</div>
      <h1 className="font-serif text-3xl mb-6" style={{ color: "var(--burgundy)" }}>Your monthly support.</h1>
      {loading ? null : !gift ? (
        <div className="rounded-2xl border p-8 text-center" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }} data-testid="manage-giving-invalid">
          <p className="font-serif text-lg" style={{ color: "var(--burgundy)" }}>This link is not valid.</p>
          <Link to="/give" className="ink-link text-sm" style={{ color: "var(--burgundy)" }}>Back to Give →</Link>
        </div>
      ) : (
        <>
          <p className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>Supporter: {gift.email_hint}</p>
          <GiftStatusCard gift={gift}
            onCancel={async () => { await api.post(`/giving/manage/${token}/cancel`); await load(); }}
            onPortal={async () => (await api.post(`/giving/manage/${token}/portal`, { return_url: window.location.href })).data.url} />
          <p className="mt-6 text-xs" style={{ color: "var(--text-muted)" }}>Keep this link private. Anyone with it can view or cancel this monthly support.</p>
        </>
      )}
    </div>
  );
}
